import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { ChapterBridge } from "./ChapterRow";

/**
 * Chapter III — FROM THE SEED TO THE SMOKE
 *
 * The educational centerpiece of the manuscript. Nine chronological steps,
 * each rendered as a full editorial manuscript spread (NOT a thumbnail
 * gallery). Plates alternate left/right for visual rhythm, and the final
 * step is a centered hero spread — the emotional climax of the journey.
 *
 * Plates are full-resolution archival engravings served from the backend
 * (`/api/media/{slug}.png`). Each plate is paired tightly with its
 * typography column so the text reads as the artwork's caption, not as
 * detached commentary.
 */

const API_MEDIA = `${process.env.REACT_APP_BACKEND_URL || ""}/api/media`;

// Plates currently approved for display. Steps not listed here render as
// clean text-only editorial spreads — no empty placeholders, no broken
// images, just generous breathing space until new archival plates land.
const PLATE_BY_STEP = {
  "02": "02-cultivation",
  "03": "03-harvest",
  "04": "04-curing-barn",
};

export default function ChapterJourney() {
  const { t, lang } = useI18n();
  const steps = t("journey.steps") || [];

  return (
    <section
      id="journey"
      data-testid="chapter-journey"
      className="relative bg-pearl text-tobacco py-20 md:py-28 border-t border-tobacco/10 overflow-hidden"
    >
      <div className="max-w-[1560px] mx-auto px-6 md:px-12">
        {/* ─── Chapter header ─────────────────────────────────────────── */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <div
              data-testid="journey-eyebrow"
              className="font-mono text-[10px] md:text-[10.5px] tracking-widest2 uppercase text-tobacco/65 mb-5"
            >
              {t("journey.eyebrow")}
            </div>
            <h2
              data-testid="journey-title"
              className="font-display italic text-tobacco leading-[0.96] text-[44px] md:text-[64px] lg:text-[80px] font-normal max-w-[18ch]"
            >
              {t("journey.title")}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p
              data-testid="journey-intro"
              className="text-tobacco/85 text-[15px] md:text-[16px] leading-[1.7] font-normal max-w-[48ch]"
            >
              {t("journey.intro")}
            </p>
            <p className="mt-5 text-tobacco/70 italic font-display text-[17px] md:text-[18px] leading-snug max-w-[44ch]">
              {t("journey.pull")}
            </p>
          </div>
        </div>

        {/* ─── 9 manuscript spreads ──────────────────────────────────── */}
        <div className="space-y-24 md:space-y-32">
          {steps.slice(0, 9).map((step, i) => {
            const plate = PLATE_BY_STEP[step.n] || null;
            const isClimax = i === 8;
            const isImageRight = !isClimax && i % 2 === 0;
            return (
              <ManuscriptSpread
                key={step.n}
                step={step}
                plate={plate}
                index={i}
                isImageRight={isImageRight}
                isClimax={isClimax}
                lang={lang}
              />
            );
          })}
        </div>

        {/* ─── Bridge to Master Blender ──────────────────────────────── */}
        <div className="mt-20 md:mt-28 border-t border-tobacco/15 pt-5 flex flex-wrap items-baseline justify-between gap-3 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
          <ChapterBridge
            left={t("journey.bridge_left")}
            right={t("journey.bridge_right")}
            testid="bridge-iii-to-iv"
            onClick={() =>
              document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" })
            }
          />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Manuscript spread ─────────────────────────── */
function ManuscriptSpread({ step, plate, index, isImageRight, isClimax, lang }) {
  // ── Climax (Step 09) ─ centered hero spread; if no plate, lean fully on
  //    typography — a final epigraph-style reveal.
  if (isClimax) {
    return (
      <article
        data-testid={`journey-step-${step.n}`}
        className="flex flex-col items-center text-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="font-mono text-[10px] md:text-[10.5px] tracking-widest2 uppercase text-tobacco/55 mb-5"
        >
          {lang === "es"
            ? "Capítulo III · Final · La Recompensa"
            : "Chapter III · Final · The Reward"}
        </motion.div>

        {plate ? (
          <div className="w-full max-w-[1320px]">
            <Figure step={step} plate={plate} index={index} />
          </div>
        ) : (
          <span className="block w-16 h-px bg-tobacco/35 mb-2" />
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: 0.2 }}
          className={`${plate ? "mt-12 md:mt-14" : "mt-2"} max-w-[58ch] mx-auto`}
        >
          <div className="font-mono text-[10.5px] md:text-[11px] tracking-widest2 uppercase text-tobacco/85 font-medium">
            {step.n} · {step.k}
          </div>
          <h3 className="mt-3 font-display italic text-tobacco leading-[1.0] text-[40px] md:text-[56px] lg:text-[68px]">
            {step.t}
          </h3>
          <p className="mt-7 text-tobacco/80 text-[15.5px] md:text-[16.5px] leading-[1.75] font-normal">
            {step.d}
          </p>
          <span className="block w-14 h-px bg-tobacco/35 mx-auto mt-9" />
        </motion.div>
      </article>
    );
  }

  // ── Standard step WITHOUT a plate ─ clean centered editorial card.
  //    Generous breathing space, narrow column width, no empty image gap.
  if (!plate) {
    return (
      <article
        data-testid={`journey-step-${step.n}`}
        className="flex flex-col items-center text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 1.0, ease: "easeOut" }}
          className="max-w-[58ch]"
        >
          <div className="flex items-baseline justify-between w-full mb-6 font-mono text-[10px] tracking-widest2 uppercase">
            <span className="text-tobacco/55">Step · {step.n}</span>
            <span className="text-tobacco/30">{step.n} / 09</span>
          </div>
          <div className="font-mono text-[10.5px] md:text-[11px] tracking-widest2 uppercase text-tobacco font-medium">
            {step.k}
          </div>
          <h3 className="mt-3 font-display italic text-tobacco leading-[1.05] text-[30px] md:text-[40px] lg:text-[46px]">
            {step.t}
          </h3>
          <p className="mt-6 text-tobacco/80 text-[15px] md:text-[16px] leading-[1.78] font-normal">
            {step.d}
          </p>
          <span className="block w-12 h-px bg-tobacco/30 mx-auto mt-8" />
        </motion.div>
      </article>
    );
  }

  // ── Standard step WITH a plate ─ 12-col editorial spread.
  const plateOrder = isImageRight
    ? "md:order-2 md:col-start-6"
    : "md:order-1 md:col-start-1";
  const textOrder = isImageRight
    ? "md:order-1 md:col-start-1 md:row-start-1"
    : "md:order-2 md:col-start-9 md:row-start-1";

  return (
    <article
      data-testid={`journey-step-${step.n}`}
      className="grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-8 items-center"
    >
      <div className={`col-span-12 md:col-span-7 ${plateOrder}`}>
        <Figure step={step} plate={plate} index={index} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.0, delay: 0.15, ease: "easeOut" }}
        className={`col-span-12 md:col-span-4 ${textOrder}`}
      >
        <div className="flex items-baseline justify-between border-b border-tobacco/25 pb-2 mb-6 font-mono text-[10px] tracking-widest2 uppercase">
          <span className="text-tobacco/65">Step · {step.n}</span>
          <span className="text-tobacco/35">{step.n} / 09</span>
        </div>
        <div className="font-mono text-[10.5px] md:text-[11px] tracking-widest2 uppercase text-tobacco font-medium">
          {step.k}
        </div>
        <h3 className="mt-2 font-display italic text-tobacco leading-[1.05] text-[28px] md:text-[36px] lg:text-[40px]">
          {step.t}
        </h3>
        <p className="mt-5 text-tobacco/80 text-[14.5px] md:text-[15.5px] leading-[1.75] font-normal">
          {step.d}
        </p>
        <span className="block w-12 h-px bg-tobacco/30 mt-7" />
      </motion.div>
    </article>
  );
}

function Figure({ step, plate, index }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.4, ease: "easeOut" }}
      data-testid={`journey-plate-${step.n}`}
      className="w-full"
    >
      <div className="w-full overflow-hidden">
        <img
          src={`${API_MEDIA}/${plate}.png`}
          alt={`${step.k} — ${step.t}`}
          loading={index < 2 ? "eager" : "lazy"}
          decoding="async"
          className="w-full h-auto block select-none pointer-events-none"
        />
      </div>
      <figcaption className="mt-3 flex items-baseline justify-between font-mono text-[9px] md:text-[10px] tracking-widest2 uppercase text-tobacco/55">
        <span>Plate · {step.n}</span>
        <span className="hidden md:inline italic font-display text-[13px] text-tobacco/70 normal-case tracking-normal">
          {step.k}
        </span>
        <span>Folio · {step.n}/09</span>
      </figcaption>
    </motion.figure>
  );
}
